import { config } from '_appConfig'
import { FC, useCallback } from 'react'

import { clearPortalSession } from 'components/contentspace/sessionCookie'
import Badge from 'components/uiKit/Badge'
import Icon from 'components/uiKit/Icon'
import Link from 'components/uiKit/Link'
import Progress, { ProgressType } from 'components/uiKit/Progress'

import { usePortalParams } from '../../hooks/usePortalParams'
import UserMenu from '../UserMenu'
import * as s from './PlayerHeader.module.scss'

interface IPlayerHeaderProps {
  backUrl: string
  backRedirectUrl?: string
  courseName: string
  progress?: number
  userName?: string
  isDebug?: boolean
}

const PlayerHeader: FC<IPlayerHeaderProps> = ({
  backUrl,
  backRedirectUrl,
  courseName,
  progress,
  userName,
  isDebug,
}) => {
  const { portalId } = usePortalParams()
  const logoutRedirectUrl = backRedirectUrl || backUrl

  const handleLogout = useCallback(async () => {
    if (portalId) {
      clearPortalSession(portalId)
      try {
        await fetch(`${config.io.contentspaceEndpoint}/public/portal/${portalId}/logout`, {
          method: 'POST',
          credentials: 'include',
        })
      } catch {
        // Best-effort: BE session cleanup failure must not block the logout redirect.
      }
    }
    window.location.href = logoutRedirectUrl
  }, [portalId, logoutRedirectUrl])

  return (
    <header className={s.root}>
      <div className={s.left}>
        <Link name='backToPortal' styleType='free' to={backUrl}>
          <Icon className={s.backButton} name='back20Px' size={20} />
        </Link>
        <h2 className={s.courseName}>{courseName}</h2>
      </div>

      <div className={s.right}>
        {isDebug && <Badge color='red' count='DEBUG' />}
        {progress !== undefined && (
          <div className={s.progressWrapper}>
            <div className={s.progressBar}>
              <Progress name='courseProgress' percent={progress} styleType={ProgressType.line} />
            </div>
            <span className={s.progressPercent}>{progress}%</span>
          </div>
        )}
        {userName && (
          <>
            <div className={s.separator} />
            <UserMenu onLogout={handleLogout}>
              <span className={s.userMenuTrigger}>
                <span className={s.userName}>{userName}</span>
              </span>
            </UserMenu>
          </>
        )}
      </div>
    </header>
  )
}

export default PlayerHeader
